import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';

const FADE_SWITCH_DURATION_MS = 220;
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

interface FadeSwitchProps {
  viewKey: string;
  children: ReactNode;
  className?: string;
  childClassName?: string;
  enterAnimationClassName?: string;
  exitAnimationClassName?: string;
  durationMs?: number;
}

interface ExitingView {
  key: string;
  children: ReactNode;
}

export function FadeSwitch({
  viewKey,
  children,
  className = 'relative',
  childClassName = '',
  enterAnimationClassName = 'animate-app-fade-in',
  exitAnimationClassName = 'animate-app-fade-out',
  durationMs = FADE_SWITCH_DURATION_MS,
}: FadeSwitchProps) {
  const previousKeyRef = useRef(viewKey);
  const previousChildrenRef = useRef(children);
  const timeoutRef = useRef<number | null>(null);
  const [exitingView, setExitingView] = useState<ExitingView | null>(null);

  useIsomorphicLayoutEffect(() => {
    if (previousKeyRef.current !== viewKey) {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }

      setExitingView({
        key: previousKeyRef.current,
        children: previousChildrenRef.current,
      });

      timeoutRef.current = window.setTimeout(() => {
        setExitingView(null);
        timeoutRef.current = null;
      }, durationMs);
    }

    previousKeyRef.current = viewKey;
    previousChildrenRef.current = children;
  }, [children, viewKey, durationMs]);

  useIsomorphicLayoutEffect(() => {
    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div className={className}>
      {exitingView && (
        <div key={`exiting-${exitingView.key}`} className={`pointer-events-none absolute inset-x-0 top-0 ${exitAnimationClassName} ${childClassName}`}>
          {exitingView.children}
        </div>
      )}
      <div key={viewKey} className={`${enterAnimationClassName} ${childClassName}`}>
        {children}
      </div>
    </div>
  );
}
